import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import '@/App.css';
import LandingPage from './pages/LandingPage';
import RegisterPage from './pages/auth/RegisterPage';
import LoginPage from './pages/auth/LoginPage';
import ProviderOnboarding from './pages/provider/ProviderOnboarding';
import ProviderDashboard from './pages/provider/ProviderDashboard';
import CustomerDashboard from './pages/customer/CustomerDashboard';
import SearchPage from './pages/customer/SearchPage';
import ProviderDetailPage from './pages/customer/ProviderDetailPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import CategoriesPage from './pages/CategoriesPage';
import SupportPage from './pages/SupportPage';
import ForCustomers from './pages/ForCustomers';
import ForProviders from './pages/ForProviders';
import ForBusiness from './pages/ForBusiness';
import EventConfigurator from './pages/EventConfigurator';
import Inspiration from './pages/Inspiration';
import PricingPage from './pages/PricingPage';
import Impressum from './pages/legal/Impressum';
import Datenschutz from './pages/legal/Datenschutz';
import AGB from './pages/legal/AGB';
import Widerruf from './pages/legal/Widerruf';
import PasswordReset from './pages/auth/PasswordReset';
import PasswordResetConfirm from './pages/auth/PasswordResetConfirm';
import VerifyEmail from './pages/auth/VerifyEmail';
import ReviewSubmissionPage from './pages/public/ReviewSubmissionPage';
import MaintenancePage from './pages/MaintenancePage';
import CookieBanner from './components/CookieBanner';
import ScrollToTop from './components/ScrollToTop';
import { Toaster } from './components/ui/sonner';
import { UnreadMessagesProvider } from './hooks/useUnreadMessages';
import { loadGoogleTag } from './utils/gtag';

// Routes that bypass maintenance mode (auth flows via email links + admin login)
const MAINTENANCE_BYPASS_PATHS = [
  '/passwort-zuruecksetzen',
  '/verify-email',
  '/passwort-vergessen',
  '/password-reset',
  '/login',
];

const shouldBypassMaintenance = () => {
  const currentPath = window.location.pathname;
  return MAINTENANCE_BYPASS_PATHS.some(path => currentPath.startsWith(path));
};

// Check if current user is an admin (reads from localStorage)
const isAdminUser = () => {
  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      return user && user.role === 'admin';
    }
  } catch (e) {
    return false;
  }
  return false;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Wartungsmodus Check
  const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true';

  useEffect(() => {
    if (localStorage.getItem('cookieConsent') === 'accepted') {
      loadGoogleTag();
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const handleUpdateUser = (updatedFields) => {
    const updatedUser = { ...user, ...updatedFields };
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Lädt...</div>
      </div>
    );
  }
  
  // Wartungsmodus aktiviert (but allow admins and certain auth routes)
  if (isMaintenanceMode && !shouldBypassMaintenance() && !isAdminUser()) {
    return <MaintenancePage />;
  }

  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <UnreadMessagesProvider user={user}>
          <ScrollToTop />
          <CookieBanner />
          <Routes>
          <Route path="/" element={<LandingPage user={user} onLogout={handleLogout} />} />
          <Route path="/kategorien" element={<CategoriesPage user={user} onLogout={handleLogout} />} />
          <Route path="/support" element={<SupportPage user={user} onLogout={handleLogout} />} />
          <Route path="/for-customers" element={<ForCustomers user={user} onLogout={handleLogout} />} />
          <Route path="/for-providers" element={<ForProviders user={user} onLogout={handleLogout} />} />
          <Route path="/for-business" element={<ForBusiness user={user} onLogout={handleLogout} />} />
          <Route path="/event-konfigurator" element={<EventConfigurator user={user} onLogout={handleLogout} />} />
          <Route path="/inspiration" element={<Inspiration user={user} onLogout={handleLogout} />} />
          <Route path="/pricing" element={<PricingPage user={user} onLogout={handleLogout} />} />
          <Route path="/impressum" element={<Impressum user={user} onLogout={handleLogout} />} />
          <Route path="/datenschutz" element={<Datenschutz user={user} onLogout={handleLogout} />} />
          <Route path="/agb" element={<AGB user={user} onLogout={handleLogout} />} />
          <Route path="/widerruf" element={<Widerruf user={user} onLogout={handleLogout} />} />
          <Route path="/passwort-vergessen" element={<PasswordReset user={user} onLogout={handleLogout} />} />
          <Route path="/password-reset" element={<PasswordReset user={user} onLogout={handleLogout} />} />
          <Route path="/passwort-zuruecksetzen" element={<PasswordResetConfirm user={user} onLogout={handleLogout} />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/bewertung/:invitationCode" element={<ReviewSubmissionPage user={user} onLogout={handleLogout} />} />
          <Route path="/register" element={<RegisterPage onLogin={handleLogin} />} />
          <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
          <Route path="/search" element={<SearchPage user={user} onLogout={handleLogout} />} />
          
          {/* Public Provider Profile - SEO-friendly URL */}
          <Route path="/p/:slugWithId" element={<ProviderDetailPage user={user} onLogout={handleLogout} />} />
          
          {/* Legacy route - redirect to new format */}
          <Route path="/provider/:providerId" element={<ProviderDetailPage user={user} onLogout={handleLogout} />} />
          
          {/* Provider Routes */}
          <Route
            path="/provider/onboarding"
            element={
              user && user.role === 'provider' ? (
                <ProviderOnboarding user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/provider/dashboard"
            element={
              user && user.role === 'provider' ? (
                <ProviderDashboard user={user} onLogout={handleLogout} onUpdateUser={handleUpdateUser} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          
          {/* Customer Routes */}
          <Route
            path="/customer/dashboard"
            element={
              user && user.role === 'customer' ? (
                <CustomerDashboard user={user} onLogout={handleLogout} onUpdateUser={handleUpdateUser} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          
          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              user && user.role === 'admin' ? (
                <AdminDashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
        </Routes>
        <Toaster position="top-right" />
        </UnreadMessagesProvider>
      </BrowserRouter>
    </div>
    </HelmetProvider>
  );
}

export default App;
